import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useNotifications } from '../context/NotificationContext';
import { useAuth } from '../context/AuthContext';
import { selectTeams, selectSelectedTeamId, selectSportsClubId } from '../store/userSlice';
import { PreseasonGameControllerService, TeamControllerService } from '../api';
import { toast } from 'react-toastify';
import './Notifications.css';

function Notifications() {
  const { notifications, loading, loadingMore, hasMore, fetchNotifications, fetchMoreNotifications, markAsRead } = useNotifications();
  const { hasRole } = useAuth();
  const isSportsClub = hasRole('SPORTS_CLUB');
  const reduxTeams = useSelector(selectTeams) || [];
  const selectedTeamId = useSelector(selectSelectedTeamId);
  const sportsClubId = useSelector(selectSportsClubId);

  const [sportsClubTeams, setSportsClubTeams] = useState([]);

  // Pobierz drużyny z API dla sports club
  useEffect(() => {
    if (!isSportsClub || !sportsClubId) return;
    TeamControllerService.getTeamsBySportsClubId(sportsClubId)
      .then((data) => setSportsClubTeams((data || []).map(t => ({ id: t.teamId, name: t.name }))))
      .catch(() => setSportsClubTeams([]));
  }, [isSportsClub, sportsClubId]);

  // Dla coacha: drużyny z Redux (mają pole id, teamName), dla sports club: z API
  const teams = isSportsClub
    ? sportsClubTeams
    : reduxTeams.map(t => ({ id: t.id, name: t.teamName || t.name }));

  const [acceptModalOpen, setAcceptModalOpen] = useState(false);
  const [acceptModalNotification, setAcceptModalNotification] = useState(null);
  const [acceptModalTeamId, setAcceptModalTeamId] = useState(null);
  const [acceptModalLoading, setAcceptModalLoading] = useState(false);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const handleMarkAsRead = async (notificationId) => {
    const success = await markAsRead(notificationId);
    if (success) {
      toast.success('Oznaczono jako przeczytane');
    } else {
      toast.error('Nie udało się oznaczyć powiadomienia');
    }
  };

  const getNotificationTypeLabel = (type) => {
    const labels = {
      'NEW_PENDING_PRESEASON_GAME': 'Nowa propozycja sparingu',
      'PRESEASON_GAME_ACCEPTED': 'Sparing zaakceptowany',
      'PRESEASON_GAME_DATE_CHANGE_REQUEST': 'Prośba o zmianę terminu',
      'PRESEASON_GAME_DATE_CHANGE_REQUEST_ACCEPTED': 'Zmiana terminu zaakceptowana',
      'PRESEASON_GAME_DELETED': 'Sparing usunięty',
      'NEW_MESSAGE': 'Nowa wiadomość',
      'NEW_USER': 'Nowy użytkownik',
      'LAST_MINUTE_PRESEASON_GAME_SEARCH': 'Pilne poszukiwanie sparingu',
    };
    return labels[type] || type;
  };

  const getPriorityClass = (priority) => {
    return `priority-${priority?.toLowerCase() || 'low'}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('pl-PL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const openAcceptModal = (notification) => {
    let preselectedTeamId = null;
    if (teams.length === 1) {
      preselectedTeamId = teams[0].id;
    } else if (selectedTeamId) {
      preselectedTeamId = selectedTeamId;
    } else if (teams.length > 0) {
      preselectedTeamId = teams[0].id;
    }
    setAcceptModalNotification(notification);
    setAcceptModalTeamId(preselectedTeamId);
    setAcceptModalOpen(true);
  };

  const closeAcceptModal = () => {
    setAcceptModalOpen(false);
    setAcceptModalNotification(null);
    setAcceptModalTeamId(null);
    setAcceptModalLoading(false);
  };

  const handleAcceptLastMinute = async () => {
    if (!acceptModalNotification || !acceptModalTeamId) return;
    setAcceptModalLoading(true);
    try {
      await PreseasonGameControllerService.approveLastMinutePreseasonGame(
        acceptModalNotification.relatedEntityId,
        acceptModalTeamId
      );
      toast.success('Sparing został zaakceptowany!');
      await markAsRead(acceptModalNotification.id);
      closeAcceptModal();
      fetchNotifications();
    } catch (error) {
      toast.error(error?.body?.message || 'Nie udało się zaakceptować sparingu');
    } finally {
      setAcceptModalLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="notifications-container">
        <div className="notifications-loading">Ładowanie powiadomień...</div>
      </div>
    );
  }

  return (
    <div className="notifications-container">
      <h1 className="notifications-title">Powiadomienia</h1>

      {notifications.length === 0 ? (
        <div className="notifications-empty">
          <span className="empty-icon">🔔</span>
          <p>Brak powiadomień</p>
        </div>
      ) : (
        <>
          <div className="notifications-list">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                className={`notification-card ${notification.isRead ? 'read' : 'unread'} ${getPriorityClass(notification.priority)}`}
              >
                <div className="notification-header">
                  <span className="notification-date">
                    {formatDate(notification.createdAt)}
                  </span>
                </div>

                <h3 className="notification-title">{notification.title}</h3>
                <p className="notification-message">{notification.message}</p>

                <div className="notification-footer">
                  <div className="notification-actions">
                    {notification.type === 'NEW_USER' && (
                      <Link to="/user-management" className="action-link-button">
                        Zarządzaj użytkownikami
                      </Link>
                    )}
                    {(notification.type === 'NEW_PENDING_PRESEASON_GAME' || notification.type === 'PRESEASON_GAME_DATE_CHANGE_REQUEST') && (
                      <Link to="/sports-club-received-matches" className="action-link-button">
                        Otrzymane zapytania
                      </Link>
                    )}
                    {(notification.type === 'PRESEASON_GAME_ACCEPTED' || notification.type === 'PRESEASON_GAME_DATE_CHANGE_REQUEST_ACCEPTED') && (
                      <Link to="/preseason-games-plans" className="action-link-button">
                        Twoje plany
                      </Link>
                    )}
                    {notification.type === 'LAST_MINUTE_PRESEASON_GAME_SEARCH' && !notification.isRead && teams.length > 0 && (
                      <button
                        className="action-link-button accept-last-minute-button"
                        onClick={() => openAcceptModal(notification)}
                      >
                        Akceptuj sparing
                      </button>
                    )}
                    {!notification.isRead && (
                      <button
                        className="mark-read-button"
                        onClick={() => handleMarkAsRead(notification.id)}
                      >
                        Oznacz jako przeczytane
                      </button>
                    )}
                    {notification.isRead && (
                      <span className="read-status">Przeczytane</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {hasMore && (
            <div className="load-more-container">
              <button
                className="load-more-button"
                onClick={fetchMoreNotifications}
                disabled={loadingMore}
              >
                {loadingMore ? 'Ładowanie...' : 'Załaduj więcej'}
              </button>
            </div>
          )}
        </>
      )}

      {acceptModalOpen && acceptModalNotification && (
        <div className="last-minute-modal-overlay" onClick={closeAcceptModal}>
          <div className="last-minute-modal" onClick={(e) => e.stopPropagation()}>
            <div className="last-minute-modal-header">
              Akceptacja sparingu
            </div>
            <div className="last-minute-modal-body">
              <p>{acceptModalNotification.message}</p>
              {teams.length > 1 && (
                <select
                  className="last-minute-team-select"
                  value={acceptModalTeamId || ''}
                  onChange={(e) => setAcceptModalTeamId(Number(e.target.value))}
                >
                  {teams.map((team) => (
                    <option key={team.id} value={team.id}>
                      {team.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div className="last-minute-modal-footer">
              <button
                className="cancel-btn"
                onClick={closeAcceptModal}
                disabled={acceptModalLoading}
              >
                Anuluj
              </button>
              <button
                className="confirm-btn"
                onClick={handleAcceptLastMinute}
                disabled={acceptModalLoading || !acceptModalTeamId}
              >
                {acceptModalLoading ? 'Akceptowanie...' : 'Akceptuj'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Notifications;
